import React, { useState } from "react";
import { toast } from "sonner";
import { StickyNote, X } from "lucide-react";
import { api } from "../lib/api";

// Out-of-character rules for the AI that apply to every reply until removed.
// Notes typed in the composer as `(AI, ...)` land here automatically.
export const StandingNotesPanel = ({ storyId, notes, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };
  const add = () => {
    if (draft.trim().length < 3) return;
    run(() => api.post(`/stories/${storyId}/standing-notes`, { text: draft.trim() }), "Standing note added").then(() => setDraft(""));
  };
  return (
    <div data-testid="standing-notes-panel" className="border border-stone-800/60 bg-stone-900/30 rounded-2xl p-3 space-y-2">
      <button type="button" data-testid="standing-notes-toggle" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-1 py-1 text-stone-500 hover:text-rose-300 font-sans-ui text-[9px] uppercase tracking-[0.25em] transition">
        <span className="flex items-center gap-2">
          <StickyNote className="w-3 h-3" strokeWidth={1.5} />
          {open ? "▾" : "▸"} Standing notes ({notes.length})
        </span>
        <span className="text-stone-500 normal-case tracking-normal text-[10px]">rules for the AI</span>
      </button>
      {open && (
        <div className="space-y-2 px-1 pt-1">
          <p className="font-serif-reading italic text-[11px] text-stone-400">
            Tip: type <span className="text-rose-300 not-italic">(AI, stop mentioning the leather jacket)</span> in your line and it's saved here, hidden from the scene, and followed every reply until you remove it.
          </p>
          {notes.length === 0 && <p data-testid="standing-notes-empty" className="font-serif-reading italic text-[11px] text-stone-500">No standing notes yet.</p>}
          {notes.map(n => (
            <div key={n.id} data-testid={`standing-note-${n.id}`} className="flex items-start justify-between gap-2 border-t border-stone-800/60 pt-1.5">
              <span className="font-serif-reading text-xs text-stone-200">{n.text}</span>
              <button type="button" data-testid={`standing-note-remove-${n.id}`} disabled={busy} aria-label="Remove note"
                onClick={() => run(() => api.delete(`/stories/${storyId}/standing-notes/${n.id}`), "Note removed")}
                className="text-stone-500 hover:text-rose-300 transition disabled:opacity-40">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <input data-testid="standing-note-input" value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="Add a rule, e.g. Jayne isn't wearing the jacket anymore"
              className="flex-1 bg-stone-900 border border-stone-800 text-stone-200 font-serif-reading text-xs px-2 py-1.5 rounded-sm focus:outline-none focus:border-rose-800 placeholder:text-stone-600" />
            <button type="button" data-testid="standing-note-add-btn" onClick={add} disabled={busy || draft.trim().length < 3}
              className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-rose-300 px-3 border border-rose-800 hover:bg-rose-950/40 disabled:opacity-40 transition">
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
